import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { INK } from "../../../theme/tokens";
import { SystemValidationTextField, type ValidationTextFieldProps } from "../../UIToolkit/Inputs/SystemValidationTextField";
import { AnnieStudioSection } from "./AnnieStudioSection";
import type { AnnieNameDraft } from "./useAnnieNameDraft";

type Props = {
	name: AnnieNameDraft;
	defaultName: string;
	disabled: boolean;
};

/** Annie's name for this user. Empty keeps the standard name; the preview follows as they type. */
export const AnnieStudioNameField: FC<Props> = ({ name, defaultName, disabled }) => {
	const { t } = useTranslation();
	// Only an invalid name gets a verdict, so the hint keeps its readable ink the rest of the time.
	const invalid: ValidationTextFieldProps | undefined = name.isValid
		? undefined
		: {
				validationCallback: () => false,
				helperText: { invalid: t("annie.studio.name.invalid") },
				performValidation: "always",
				showValidWhenValid: "never",
			};

	return (
		<AnnieStudioSection title={t("annie.studio.name.label")}>
			{(labelId) => (
				<SystemValidationTextField
					value={name.value}
					onChange={(event) => name.setValue(event.target.value)}
					onClear={() => name.setValue("")}
					placeholder={defaultName}
					disabled={disabled}
					validation={invalid}
					defaultHelperText={t("annie.studio.name.hint", { defaultName })}
					helperTextColor={INK.MUTED}
					injectedProps={{ slotProps: { htmlInput: { "aria-labelledby": labelId } } }}
					fullWidth
					dataTestId="annie-studio-name"
				/>
			)}
		</AnnieStudioSection>
	);
};
